/**
 * MembersPanel — tenant detail Tenant administrators (key `administrators`, TMMPDM-0004).
 * Lists people who administer this tenant via `listStaff`
 * (`GET …/staff`). Assignable roles are OWNER | ADMIN | STAFF; default add
 * is ADMIN. Never lists or invites USER. Mutations stay on
 * `…/users/{membershipId}`. A `/staff` failure uses the load-error path —
 * never falls back to `GET …/users`.
 *
 * All mutations key on `membershipId`, never userId. `LAST_OWNER_PROTECTED`
 * (409) and `ROLE_CHANGE_CROSSES_TABLES` (400) surface the server's message.
 *
 * No pagination UI: `listStaff` is called with a single generous page
 * (size 100).
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, EmptyState, ErrorState, SubmitButton, useNotify } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AppSwitch } from '@/components/ui';
import {
  addMember,
  changeRole,
  listStaff,
  removeMember,
  setMemberStatus,
} from '@/api/saas/tenants';
import { ApiError, errorCopy } from '@/api/errors';
import type { AssignableRole, TenantMemberDto } from '@/api/saas/types';
import { StatusChip } from '@/status';
import { PeopleTableContainer, PeopleToolbar } from '../partners/peopleLeafLayout';

const ASSIGNABLE_ROLES: AssignableRole[] = ['OWNER', 'ADMIN', 'STAFF'];
const SEARCH_DEBOUNCE_MS = 250;

interface MembersPanelProps {
  tenantId: number;
  /** Called after `addMember` returns an invitation (202) so the sibling InvitationsPanel refreshes. */
  onMemberInvited: () => void;
}

export function MembersPanel({ tenantId, onMemberInvited }: MembersPanelProps) {
  const { t } = useCopy();
  const notify = useNotify();
  const [members, setMembers] = useState<TenantMemberDto[] | null>(null);
  const [loading, setLoading] = useState(true);
  // Error-KIND, not a pre-translated string — translated at render below, not
  // inside the effect, so useCopy()'s `t` (a fresh reference every render)
  // never needs to sit in the effect's dependency array (see
  // useTenantIdentityRoster.ts's doc comment for the refetch-loop this avoids).
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadCause, setLoadCause] = useState<unknown>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [removeTarget, setRemoveTarget] = useState<TenantMemberDto | null>(null);
  // A refused removal is said inside its confirm, which stays open (TMM-B4).
  const [removeError, setRemoveError] = useState<string | null>(null);

  const [addEmail, setAddEmail] = useState('');
  const [addRole, setAddRole] = useState<AssignableRole>('ADMIN');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    // AUTH staff list is 0-based (same as ProductAdminController / tenant roster).
    listStaff(tenantId, { page: 0, size: 100, ...(query ? { q: query } : {}) })
      .then((res) => {
        if (cancelled) return;
        setMembers(res.content);
        setLoadFailed(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadCause(err);
        setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, reloadToken, query]);

  const refetch = () => {
    setLoading(true);
    setReloadToken((n) => n + 1);
  };

  /**
   * Shared mapping for change-role/disable/remove failures: a 409
   * `LAST_OWNER_PROTECTED` shows its catalog message; any other failure shows the
   * action's own generic message.
   */
  const describeMutationError = (err: unknown, genericFallback: string): string => {
    const apiErr = err instanceof ApiError ? err : null;
    if (apiErr?.code === 'LAST_OWNER_PROTECTED' || apiErr?.code === 'ROLE_CHANGE_CROSSES_TABLES') {
      const fallback =
        apiErr.code === 'ROLE_CHANGE_CROSSES_TABLES'
          ? t(
              'tenantDetail.members.roleChangeCrossesTables',
              'This role change is not allowed for this staff member.',
            )
          : t(
              'tenantDetail.members.lastOwnerProtected',
              "This would remove the tenant's last owner.",
            );
      return fallback;
    }
    return errorCopy(err, t, genericFallback);
  };

  const handleChangeRole = async (member: TenantMemberDto, role: AssignableRole) => {
    setActionError(null);
    setBusyId(member.membershipId);
    try {
      await changeRole(tenantId, member.membershipId, role);
      refetch();
    } catch (err) {
      setActionError(
        describeMutationError(
          err,
          t('tenantDetail.members.changeRoleError', 'Could not change role. Please try again.'),
        ),
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleStatus = async (member: TenantMemberDto, checked: boolean) => {
    setActionError(null);
    setBusyId(member.membershipId);
    try {
      await setMemberStatus(tenantId, member.membershipId, checked ? 'ACTIVE' : 'DISABLED');
      refetch();
    } catch (err) {
      setActionError(
        describeMutationError(
          err,
          t(
            'tenantDetail.members.toggleStatusError',
            'Could not update staff status. Please try again.',
          ),
        ),
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmRemove = async () => {
    const member = removeTarget;
    if (!member) return;
    setActionError(null);
    setRemoveError(null);
    setBusyId(member.membershipId);
    try {
      await removeMember(tenantId, member.membershipId);
      setRemoveTarget(null);
      refetch();
    } catch (err) {
      setRemoveError(
        describeMutationError(
          err,
          t('tenantDetail.members.removeError', 'Could not remove staff. Please try again.'),
        ),
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleAddMember = async () => {
    setAddError(null);
    if (addEmail.trim() === '') {
      setAddError(t('tenantDetail.members.addEmailRequired', 'Email is required.'));
      return;
    }
    setAdding(true);
    try {
      const result = await addMember(tenantId, { email: addEmail.trim(), role: addRole });
      setAddEmail('');
      if (result.kind === 'member') {
        notify.success(t('tenantDetail.members.addSuccessMember', 'Staff added.'));
        refetch();
      } else {
        notify.success(t('tenantDetail.members.addSuccessInvitation', 'Invite sent.'));
        onMemberInvited();
      }
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : null;
      if (apiErr?.code === 'ALREADY_ACTIVE_MEMBER') {
        setAddError(
          t(
            'tenantDetail.members.addErrorAlreadyActive',
            'This person is already an active staff member.',
          ),
        );
      } else if (apiErr?.code === 'INVITATION_ALREADY_PENDING') {
        setAddError(
          t(
            'tenantDetail.members.addErrorAlreadyPending',
            'An invitation is already pending for this email.',
          ),
        );
      } else {
        setAddError(
          errorCopy(
            err,
            t,
            t('tenantDetail.members.addError', 'Could not add staff. Please try again.'),
          ),
        );
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {t('tenantDetail.members.title', 'Tenant administrators')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('tenantDetail.members.help', 'Who administers this tenant.')}
      </Typography>

      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          void handleAddMember();
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('tenantDetail.members.addTitle', 'Add administrator')}
        </Typography>
        {addError ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {addError}
          </Alert>
        ) : null}
        <PeopleToolbar>
          <TextField
            size="small"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.preventDefault();
            }}
            label={t('resellers.users.searchLabel', 'Search users')}
            sx={{ minWidth: 260 }}
          />
          <TextField
            label={t('tenantDetail.members.addEmailLabel', 'Email')}
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
            size="small"
            sx={{ minWidth: 240 }}
          />
          <TextField
            select
            label={t('tenantDetail.members.addRoleLabel', 'Role')}
            value={addRole}
            onChange={(e) => setAddRole(e.target.value as AssignableRole)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            {ASSIGNABLE_ROLES.map((role) => (
              <MenuItem key={role} value={role}>
                {t(`tenantDetail.members.role.${role}`, role)}
              </MenuItem>
            ))}
          </TextField>
          <SubmitButton type="submit" busy={adding}>
            {t('tenantDetail.members.addButton', 'Add')}
          </SubmitButton>
        </PeopleToolbar>
      </Box>

      {loading ? <LoadingSpinner /> : null}
      {!loading && loadFailed ? (
        <ErrorState
          error={loadCause}
          message={t('tenantDetail.members.loadError', 'Could not load staff.')}
          onRetry={refetch}
        />
      ) : null}

      {!loading && !loadFailed && members ? (
        <PeopleTableContainer>
          <Table aria-label={t('tenantDetail.members.tableAriaLabel', 'Tenant staff')} size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('tenantDetail.members.col.email', 'Email')}</TableCell>
                <TableCell>{t('tenantDetail.members.col.name', 'Name')}</TableCell>
                <TableCell>{t('tenantDetail.members.col.role', 'Role')}</TableCell>
                <TableCell>{t('tenantDetail.members.col.status', 'Status')}</TableCell>
                <TableCell align="right">
                  {t('tenantDetail.members.col.actions', 'Actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <EmptyState title={t('tenantDetail.members.empty', 'No administrators yet.')} />
                  </TableCell>
                </TableRow>
              ) : (
                members.map((member) => {
                  const busy = busyId === member.membershipId;
                  const roleAssignable = (ASSIGNABLE_ROLES as string[]).includes(member.role);
                  return (
                    <TableRow key={member.membershipId}>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{member.name ?? '—'}</TableCell>
                      <TableCell>
                        {roleAssignable ? (
                          <TextField
                            select
                            size="small"
                            variant="standard"
                            label={t('tenantDetail.members.changeRoleLabel', 'Role for {{email}}', {
                              email: member.email,
                            })}
                            value={member.role}
                            disabled={busy}
                            onChange={(e) =>
                              void handleChangeRole(member, e.target.value as AssignableRole)
                            }
                            sx={{ minWidth: 110 }}
                          >
                            {ASSIGNABLE_ROLES.map((role) => (
                              <MenuItem key={role} value={role}>
                                {t(`tenantDetail.members.role.${role}`, role)}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <Chip
                            size="small"
                            label={t(`tenantDetail.members.role.${member.role}`, member.role)}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {member.status === 'INVITED' ? (
                          <StatusChip
                            status="INVITED"
                            label={t('tenantDetail.members.status.INVITED', 'Invited')}
                          />
                        ) : (
                          <AppSwitch
                            label={t('tenantDetail.members.disableLabel', 'Enabled')}
                            checked={member.status === 'ACTIVE'}
                            onChange={(checked) => void handleToggleStatus(member, checked)}
                            disabled={busy}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          disabled={busy}
                          onClick={() => setRemoveTarget(member)}
                        >
                          {t('tenantDetail.members.removeButton', 'Remove')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </PeopleTableContainer>
      ) : null}

      <ConfirmDialog
        open={removeTarget !== null}
        title={t('tenantDetail.members.removeConfirmTitle', 'Remove staff?')}
        consequence={t(
          'tenantDetail.members.removeConfirmBody',
          "This removes {{email}} from this tenant's staff. Their account is not deleted.",
          { email: removeTarget?.email ?? '' },
        )}
        confirmLabel={t('tenantDetail.members.removeButton', 'Remove')}
        destructive
        busy={busyId !== null}
        error={removeError}
        onConfirm={() => void handleConfirmRemove()}
        onCancel={() => {
          setRemoveTarget(null);
          setRemoveError(null);
        }}
      />
    </Box>
  );
}
